import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const formatSupabaseError = (err, fallback) => {
  return [err?.message, err?.details, err?.hint].filter(Boolean).join(' ') || fallback;
};

const getTaskStatusLabel = (status) => status?.replaceAll('_', ' ');

const LawFirmTaskQueue = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [mineOnly, setMineOnly] = useState(false);

  const fetchTasks = async () => {
    if (!profile?.law_firm_id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [taskRes, userRes] = await Promise.all([
        supabase
          .from('matter_tasks')
          .select(`
            *,
            matter:matters (
              id,
              plan_type,
              status,
              client:clients (
                first_name,
                last_name,
                email
              )
            )
          `)
          .eq('law_firm_id', profile.law_firm_id)
          .order('created_at', { ascending: false }),
        supabase
          .from('profiles')
          .select('id, first_name, last_name, email, role')
          .eq('law_firm_id', profile.law_firm_id)
          .in('role', ['attorney', 'legal_staff'])
          .order('last_name', { ascending: true })
      ]);

      if (taskRes.error) throw taskRes.error;
      if (userRes.error) throw userRes.error;

      setTasks(taskRes.data || []);
      setUsers(userRes.data || []);
    } catch (err) {
      console.error('Law firm task queue error:', err);
      setError(formatSupabaseError(err, 'Unable to load task queue.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [profile]);

  const getUserName = (userId) => {
    const user = users.find((item) => item.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : 'Unassigned';
  };

  const handleStatusChange = async (task, nextStatus) => {
    try {
      setError(null);

      const { data, error: updateErr } = await supabase
        .from('matter_tasks')
        .update({
          status: nextStatus,
          completed_at: nextStatus === 'completed' ? new Date().toISOString() : null
        })
        .eq('id', task.id)
        .select('*')
        .single();

      if (updateErr) throw updateErr;

      setTasks((prev) => prev.map((item) => (item.id === data.id ? { ...item, ...data, matter: item.matter } : item)));

      await supabase.from('audit_logs').insert({
        user_id: profile.id,
        action: 'matter_task_status_updated',
        entity_type: 'matter_task',
        entity_id: task.id,
        metadata: {
          matter_id: task.matter_id,
          previous_status: task.status,
          status: nextStatus
        }
      });
    } catch (err) {
      console.error('Update task queue item error:', err);
      setError(formatSupabaseError(err, 'Unable to update task.'));
    }
  };

  const filteredTasks = tasks.filter((task) => {
    if (mineOnly && task.assigned_to !== profile?.id) return false;
    if (statusFilter === 'active') return task.status !== 'completed';
    if (statusFilter === 'all') return true;
    return task.status === statusFilter;
  });

  if (!profile?.law_firm_id) {
    return (
      <div className="p-8 text-center bg-white rounded-lg border border-slate-200">
        Law firm access requires a law firm profile assignment.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Task Queue</h1>
          <p className="text-sm text-slate-500">Matter tasks across assigned law firm plans.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <label className="inline-flex items-center text-sm text-slate-600 bg-white border border-slate-200 rounded-md px-3 py-2">
            <input
              type="checkbox"
              checked={mineOnly}
              onChange={(e) => setMineOnly(e.target.checked)}
              className="mr-2 rounded border-slate-300 text-slate-800 focus:ring-slate-500"
            />
            My tasks
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none bg-white"
          >
            <option value="active">Active</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="blocked">Blocked</option>
            <option value="completed">Completed</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
          <p className="text-sm text-red-700 font-medium">{error}</p>
        </div>
      )}

      <div className="bg-white shadow rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-20 text-center">
            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mx-auto h-10 w-10 text-slate-300 mb-4" />
            <p className="text-slate-500">Loading task queue...</p>
          </div>
        ) : filteredTasks.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {filteredTasks.map((task) => (
              <div key={task.id} className="p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-900">{task.title}</h3>
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                      {task.priority}
                    </span>
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                      {getTaskStatusLabel(task.status)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mt-2">
                    {task.matter?.client?.first_name} {task.matter?.client?.last_name} - {task.matter?.plan_type}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Assigned to {getUserName(task.assigned_to)}
                    {task.due_date && ` - Due ${format(new Date(`${task.due_date}T00:00:00`), 'MMM d, yyyy')}`}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <select
                    value={task.status}
                    onChange={(e) => handleStatusChange(task, e.target.value)}
                    className="block w-full sm:w-44 sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none bg-white"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="blocked">Blocked</option>
                    <option value="completed">Completed</option>
                  </select>
                  <button
                    onClick={() => navigate(`/law-firm/matters/${task.matter_id}`)}
                    className="inline-flex items-center justify-center px-3 py-2 text-xs font-bold uppercase tracking-widest rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                  >
                    Open Plan
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-20 text-center text-slate-500 px-4">
            <SafeIcon icon={FiIcons.FiCheckSquare} className="h-12 w-12 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No tasks found</h3>
            <p className="mt-1 text-slate-500">Tasks created on assigned plans will appear here.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LawFirmTaskQueue;
